import React, { useEffect, useMemo, useState, useRef, useContext } from "react";
import { useTranslation } from "react-i18next";

import styles from "./GoalsCard.module.css";
import { tagsPt } from "../../data/tags";
import { tagsEn } from "../../data/tags";
import { UserContext } from "../../context/UserContext";
import { updateUserFirebase } from "../../services/firebaseService";

export default function GoalsCard() {
  const { i18n, t } = useTranslation("user");
  const ALL_TAGS = i18n.language === "pt" ? tagsPt : tagsEn;
  const { userData, setUserData, user } = useContext(UserContext);
  const userTags =
    (userData && userData.preferences && userData.preferences.tags) ||
    (userData && userData.tags) ||
    [];
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState([...userTags]);
  const [query, setQuery] = useState("");
  const [saving, setSaving] = useState(false);
  const overlayRef = useRef(null);
  const searchRef = useRef(null);

  // utilitário simples para comparar arrays de strings (ordem importante)
  function arraysEqual(a = [], b = []) {
    if (a === b) return true;
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (a[i] !== b[i]) return false;
    }
    return true;
  }

  // sincroniza selected com userTags, MAS NÃO enquanto o modal estiver aberto
  useEffect(() => {
    if (open) return; // evita sobrescrever seleções do usuário enquanto estiver editando

    const current = Array.isArray(userTags) ? [...userTags] : [];

    setSelected((prev) => {
      if (arraysEqual(prev, current)) return prev; // nada mudou -> não setState
      return current; // atualiza apenas quando realmente necessário (e modal fechado)
    });
  }, [userTags, open]);

  // keyboard handling (Esc) - close modal
  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") setOpen(false);
      if (e.key === "/" && !open) {
        e.preventDefault();
        setOpen(true);
        setTimeout(() => searchRef.current?.focus(), 40);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const normalizedQuery = query.trim().toLowerCase();

  // filtered list of tags by query
  const filtered = useMemo(() => {
    if (!normalizedQuery) return ALL_TAGS;
    return ALL_TAGS.filter((t) =>
      t.label.toLowerCase().includes(normalizedQuery)
    );
  }, [normalizedQuery]);

  const MAX_TAGS = 5;

  const toggleTag = (tagKey) => {
    setSelected((prev) => {
      const already = prev.includes(tagKey);

      // se já está selecionada → desmarca
      if (already) return prev.filter((t) => t !== tagKey);

      // se não está selecionada → verifica se pode adicionar
      if (prev.length >= MAX_TAGS) {
        // feedback amigável
        alert(t("goals.alert1"));
        return prev;
      }

      return [...prev, tagKey];
    });
  };

  const clearAll = () => setSelected([]);

  async function handleSave() {
    if (!user) {
      alert(t("goals.alert2"));
      return;
    }

    setSaving(true);

    try {
      const updatedPreferences = {
        ...userData?.preferences,
        tags: selected,
      };

      // 1) grava no Firestore
      await updateUserFirebase({
        id: user.uid,
        preferences: updatedPreferences,
      });

      // 2) atualiza contexto local
      setUserData((prev) => ({
        ...prev,
        preferences: updatedPreferences,
      }));

      setOpen(false);
    } catch (err) {
      console.error("Erro salvando metas:", err);
      alert(t("goals.alert3"));
    } finally {
      setSaving(false);
    }
  }

  // close when clicking overlay
  function onOverlayClick(e) {
    if (e.target === overlayRef.current) setOpen(false);
  }

  return (
    <>
      <div className={styles.card}>
        <div className={styles.cardTop}>
          <h3 className={styles.title}>{t("goals.title")}</h3>
        </div>

        <div className={styles.tagsRow}>
          {selected.length === 0 ? (
            <div className={styles.empty}>{t("goals.noGoals")}</div>
          ) : (
            selected.map((t) => {
              const tagObj = ALL_TAGS.find((x) => x.key === t) || {
                key: t,
                label: t,
              };
              return (
                <span key={t} className={styles.chip}>
                  {tagObj.label}
                </span>
              );
            })
          )}
        </div>

        <div className={styles.actions}>
          <button className={styles.editBtn} onClick={() => setOpen(true)}>
            {t("goals.editGoals")}
          </button>
        </div>
      </div>

      {open && (
        <div
          className={styles.modalOverlay}
          ref={overlayRef}
          onMouseDown={onOverlayClick}
          role="dialog"
          aria-modal="true"
          aria-label="Editar metas"
        >
          <div className={styles.modalCard} role="document">
            <div className={styles.modalHeader}>
              <h4>{t("goals.modalHeader")}</h4>
              <button
                className={styles.modalClose}
                onClick={() => setOpen(false)}
                aria-label="Fechar modal"
              >
                ✕
              </button>
            </div>

            <div className={styles.modalBody}>
              <div className={styles.searchRow}>
                <input
                  ref={searchRef}
                  className={styles.searchInput}
                  placeholder={t("goals.modalSearch")}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  aria-label="Pesquisar tags"
                />
                <button
                  className={styles.clearBtn}
                  onClick={() => setQuery("")}
                  aria-label="Limpar pesquisa"
                >
                  ✕
                </button>
              </div>

              <div className={styles.legend}>
                <button className={styles.clearAll} onClick={clearAll}>
                  {t("goals.cleanGoals")}
                </button>
                <div className={styles.selectedCount}>
                  {selected.length}
                  {t("goals.selected")}
                </div>
              </div>

              <div className={styles.tagsGrid}>
                {filtered.map((t) => {
                  const isSelected = selected.includes(t.key);
                  return (
                    <button
                      key={t.key}
                      type="button"
                      className={`${styles.tagButton} ${
                        isSelected ? styles.tagSelected : ""
                      }`}
                      onClick={() => toggleTag(t.key)}
                      aria-pressed={isSelected}
                    >
                      <div className={styles.tagLabel}>{t.label}</div>
                      {t.meta && <div className={styles.tagMeta}>{t.meta}</div>}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className={styles.modalFooter}>
              <button
                className={styles.cancelBtn}
                onClick={() => setOpen(false)}
                disabled={saving}
              >
                {t("goals.cancelButton")}
              </button>
              <button
                className={styles.saveBtn}
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? t("goals.savingButton") : t("goals.saveButton")}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
